import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin, X, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { repGet, repDelete } from './reportsApi';
import { GREEN } from '../bfsi/bfsiShared';

export const typeLabel = (code) => (code || '').toLowerCase().replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const FILTER_LABELS = { from: 'From', to: 'To', org_unit_id: 'Org unit', track0tic_id: 'Device', trip_code: 'Trip',
  passport_short_code: 'Passport', group_by: 'Group by', device_type: 'Device type' };

export function pinChips(filters = {}) {
  return Object.entries(filters).filter(([k, v]) => k !== 'tenant_id' && v).map(([k, v]) => `${FILTER_LABELS[k] || k}: ${v}`);
}

export function PinnedReportsStrip() {
  const [pins, setPins] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    repGet('/pins').then(setPins).catch(() => setPins([]));
  }, []);

  const remove = async (p) => {
    try {
      await repDelete(`/pins/${p.id}`);
      setPins(list => list.filter(x => x.id !== p.id));
      toast.success(`Unpinned "${p.label}"`);
    } catch (e) { toast.error(e.message); }
  };

  if (!pins) return null;
  return (
    <section className="mb-6" data-testid="pinned-reports-strip">
      <div className="flex items-center gap-2 mb-3">
        <Pin size={16} style={{ color: GREEN }} />
        <h2 className="text-base md:text-lg font-semibold" style={{ color: '#1B1D1C' }}>Pinned reports</h2>
        <span className="text-xs" style={{ color: '#9a9e96' }}>shared with all Track0tic admins</span>
      </div>
      {pins.length === 0 ? (
        <p className="text-sm" style={{ color: '#9a9e96' }} data-testid="pinned-reports-empty">
          Nothing pinned yet — open <button className="underline" onClick={() => navigate('/admin/reports')}>Reports</button>, set your filters and click “Pin to dashboard”.
        </p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {pins.map(p => (
            <div key={p.id} data-testid={`pinned-report-${p.id}`}
              className="min-w-[240px] max-w-[300px] rounded-lg border bg-white p-4 flex flex-col gap-2 shrink-0 transition-shadow hover:shadow-md"
              style={{ borderColor: '#D9D6CD' }}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm leading-tight" style={{ color: '#1B1D1C' }}>{p.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#6b7069' }}>{typeLabel(p.type)}{p.tenant_name ? ` · ${p.tenant_name}` : ''}</div>
                </div>
                <button data-testid={`unpin-${p.id}`} onClick={() => remove(p)} title="Unpin"
                  className="p-1 rounded hover:bg-[#F3F1EA] transition-colors" style={{ color: '#9a9e96' }}><X size={14} /></button>
              </div>
              <div className="flex flex-wrap gap-1 min-h-[22px]">
                {pinChips(p.filters).length === 0 ? <span className="text-xs" style={{ color: '#9a9e96' }}>No filters</span>
                  : pinChips(p.filters).map(c => (
                    <span key={c} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: '#F3F1EA', color: '#4b504a' }}>{c}</span>
                  ))}
              </div>
              <button data-testid={`open-pin-${p.id}`} onClick={() => navigate(`/admin/reports?pin=${p.id}`)}
                className="mt-auto inline-flex items-center gap-1 text-sm font-medium self-start hover:underline" style={{ color: GREEN }}>
                Open report <ArrowUpRight size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
